import { useState, useCallback, useMemo } from 'react'

const DEFAULT_GUEST_COUNTS = {
  adults: 2,
  children: 0,
  infants: 0,
  pets: 0,
}

export const useGuestSelection = (
  initialGuests = 2,
  initialGuestCounts = null,
) => {
  const [guestCounts, setGuestCounts] = useState(() => {
    if (initialGuestCounts) {
      return {
        adults: Math.max(1, Number(initialGuestCounts.adults) || 1),
        children: Math.max(0, Number(initialGuestCounts.children) || 0),
        infants: Math.max(0, Number(initialGuestCounts.infants) || 0),
        pets: Math.max(0, Number(initialGuestCounts.pets) || 0),
      }
    }

    if (initialGuests && initialGuests > 0) {
      return {
        ...DEFAULT_GUEST_COUNTS,
        adults: Math.max(1, initialGuests),
      }
    }
    return DEFAULT_GUEST_COUNTS
  })

  const updateGuestCount = useCallback((type, change) => {
    setGuestCounts((prev) => {
      // If type is an object, it means we're setting multiple values at once
      if (typeof type === 'object') {
        return { ...prev, ...type }
      }
      
      // Otherwise, it's the old increment/decrement behavior
      const newCount = Math.max(0, prev[type] + change)
      // Ensure at least 1 adult
      if (type === 'adults' && newCount === 0) {
        return prev
      }
      return { ...prev, [type]: newCount }
    })
  }, [])

  // ✅ Infants are excluded from total guest count
  const totalGuests = useMemo(
    () => guestCounts.adults + guestCounts.children,
    [guestCounts.adults, guestCounts.children],
  )

  const getDisplayText = useCallback(() => {
    const parts = []

    // Add guests (adults + children)
    if (totalGuests > 0) {
      parts.push(totalGuests === 1 ? '1 guest' : `${totalGuests} guests`)
    }

    // Add infants
    if (guestCounts.infants > 0) {
      parts.push(
        guestCounts.infants === 1
          ? '1 infant'
          : `${guestCounts.infants} infants`,
      )
    }

    // Add pets
    if (guestCounts.pets > 0) {
      parts.push(guestCounts.pets === 1 ? '1 pet' : `${guestCounts.pets} pets`)
    }

    return parts.length > 0 ? parts.join(', ') : 'Add guests'
  }, [totalGuests, guestCounts.infants, guestCounts.pets])

  const resetGuests = useCallback(() => {
    setGuestCounts(DEFAULT_GUEST_COUNTS)
  }, [])

  return {
    guestCounts,
    totalGuests,
    updateGuestCount,
    getDisplayText,
    resetGuests,
  }
}
